/*
 * The account's own preferences, applied to the parts of the client that are not React state, and
 * the one write path of an account setting (`writeAccountSetting`).
 *
 * Two preferences are applied here. An account carries its own time zone (schema.md §2.1), and every
 * stamp in the interface — message times, day dividers, tooltips — is built by the memoised `Intl`
 * formatters in core/i18n/format.ts, which have to be rebuilt when the bootstrap says which zone
 * this is (W1-09's hand-over note). And the density is stamped on the document root as
 * `data-density`, the way `theme.ts` stamps `data-theme`: the tokens the two densities differ in are
 * declared under `[data-density]` in styles/tokens.css, so one attribute moves the whole rhythm and
 * no component has to know which density it is drawn in.
 *
 * Why the density follows the *account* while the theme follows the *device*: a theme has to be right
 * before the first byte of the model arrives — a white flash on a dark system is the one preference a
 * reader notices being read late — while how close the rows sit is a taste somebody has on every
 * machine they use. It costs one reflow on a cold boot, where the bootstrap's value arrives after the
 * first paint; a warm boot hydrates the stored model and already has it.
 */

import { useCallback, useEffect, useMemo } from "react"
import type { StoreApi } from "zustand"
import { DEFAULT_TIME_ZONE, setTimeZone } from "../i18n/index.ts"
import { api } from "../net/api.ts"
import type { Model } from "../store/model.ts"
import { selectSetting, type CoreSettings, type SettingKey } from "../store/selectors.ts"
import { useModel, useShell } from "./context.tsx"

/** The shell's own half of the settings registry: every key of the `core.shell.` section. */
export type ShellSettingKey = Extract<SettingKey, `core.shell.${string}`>

/**
 * Changes one of this account's settings, everywhere: the one write path of every preference, the
 * shell's, the composer's and the modules' alike.
 *
 * The model is written first, so the control that was just used answers in the same frame; the
 * server follows and announces `ui.settings.changed`, which is what carries the change to this
 * person's other devices. A failed write is deliberately quiet — the value holds for this tab and
 * the next load reads whatever the server has — which is also what makes a dismissed welcome stay
 * dismissed on an instance that is having a bad minute: a dialog over a preference nobody asked to
 * save would be worse than the drift.
 *
 * The key is a plain string because the catalogue of `core/store/selectors.ts` knows core's keys
 * only; a module's key (`aichat.density`, `chat.emoji.skin_tone`) is registered by its own server
 * half, which is what refuses a key nobody registered.
 */
export function writeAccountSetting(store: StoreApi<Model>, key: string, next: unknown): void {
	store.setState((model) => ({ settings: { ...model.settings, [key]: next } }))
	void api.put("/api/ui/settings", { settings: { [key]: next } }).catch(() => undefined)
}

/**
 * One shell setting and the way to change it, in the shape `useTheme` has: `[value, write]`.
 *
 * This is the one writer of these keys — `useSidebarCollapsed` below goes through the same path. A
 * key with two writers is a key whose value depends on which screen was used last.
 */
export function useShellSetting<K extends ShellSettingKey>(
	key: K,
): [CoreSettings[K], (next: CoreSettings[K]) => void] {
	const store = useShell().store
	const value = useModel((model) => selectSetting(model, key))
	const write = useCallback(
		(next: CoreSettings[K]) => writeAccountSetting(store, key, next),
		[store, key],
	)
	return [value, write]
}

/**
 * The sidebar sections this account has folded (VISUAL.md §7.2), kept under
 * `core.shell.sidebar_collapsed` as the section ids one space apart — the spelling
 * `internal/core/settings/keys.go` validates. Read as a set, written as the whole list: the shell
 * folds one section at a time, but the setting is one value.
 *
 * The catalogue of `core/store/selectors.ts` knows the key as a plain string — the ids in it are the
 * modules' own and core knows no closed list of them — so an account that has never folded anything
 * reads as the empty string, which is every section open.
 */
export const SIDEBAR_COLLAPSED_KEY = "core.shell.sidebar_collapsed"

export function useSidebarCollapsed(): [
	ReadonlySet<string>,
	(sectionId: string, collapsed: boolean) => void,
] {
	const [raw, write] = useShellSetting(SIDEBAR_COLLAPSED_KEY)
	const folded = useMemo(() => new Set(raw.split(" ").filter((id) => id !== "")), [raw])
	const setFolded = useCallback(
		(sectionId: string, collapsed: boolean) => {
			const next = new Set(folded)
			if (collapsed) next.add(sectionId)
			else next.delete(sectionId)
			write([...next].sort().join(" "))
		},
		[write, folded],
	)
	return [folded, setFolded]
}

export function AccountPreferences() {
	const zone = useModel((model) => model.me?.timezone ?? DEFAULT_TIME_ZONE)
	const [density] = useShellSetting("core.shell.density")

	useEffect(() => {
		setTimeZone(zone)
	}, [zone])

	// The attribute is written for the shipped density too, so that it is always the answer to "which
	// rhythm is this?"; a missing attribute would make "comfortable" a second, implicit spelling.
	useEffect(() => {
		document.documentElement.dataset.density = density
	}, [density])

	return null
}
